import React, { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { TooltipLabel } from '../ui/TooltipLabel';
import { costManagementTooltips } from '../../utils/strategicFrameworkTooltips';
import { Target, Package } from 'lucide-react';

/**
 * Cascading dropdowns for Strategic Framework linking
 * Objective → Initiative → Revenue Stream → Product/Service
 */
const StrategicFrameworkLinker = ({ form, setForm, sfHierarchy }) => {
  const [filteredInitiatives, setFilteredInitiatives] = useState([]);
  const [filteredStreams, setFilteredStreams] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);

  // Filter initiatives when objective changes
  useEffect(() => {
    if (form.linked_objective_id) {
      const objective = sfHierarchy.find(o => o.id === form.linked_objective_id);
      setFilteredInitiatives(objective?.initiatives || []);
    } else {
      setFilteredInitiatives([]);
    }
  }, [form.linked_objective_id, sfHierarchy]);

  // Filter streams when initiative changes
  useEffect(() => {
    if (form.linked_initiative_id) {
      const initiative = filteredInitiatives.find(i => i.id === form.linked_initiative_id);
      setFilteredStreams(initiative?.revenue_streams || []);
    } else {
      setFilteredStreams([]);
    }
  }, [form.linked_initiative_id, filteredInitiatives]);

  // Filter products when revenue stream changes
  useEffect(() => {
    if (form.linked_revenue_stream_id) {
      const stream = filteredStreams.find(s => s.id === form.linked_revenue_stream_id);
      setFilteredProducts(stream?.products || []);
    } else {
      setFilteredProducts([]);
    }
  }, [form.linked_revenue_stream_id, filteredStreams]);

  return (
    <div className="space-y-4 border rounded-lg p-4 bg-gray-50">
      <h4 className="font-medium flex items-center">
        <Target className="h-4 w-4 mr-2 text-blue-600" />
        Strategic Framework Linkage (Optional)
      </h4>
      <p className="text-xs text-gray-500">
        Link this cost to strategic objectives, initiatives, revenue streams, and products for better analysis and tracking
      </p>

      {/* Objective Selection */}
      <div className="space-y-2">
        <TooltipLabel 
          htmlFor="sf-objective" 
          tooltip={costManagementTooltips.strategicLinking?.objective || 'Link to strategic objective'}
        >
          Strategic Objective
        </TooltipLabel>
        <Select 
          value={form.linked_objective_id || '__none__'}
          onValueChange={(val) => {
            setForm({
              ...form,
              linked_objective_id: val === '__none__' ? '' : val,
              linked_initiative_id: '',
              linked_revenue_stream_id: '',
              linked_product_id: ''
            });
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select objective (optional)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">No Link</SelectItem>
            {sfHierarchy.map(obj => (
              <SelectItem key={obj.id} value={obj.id}>
                {obj.objective_id} - {obj.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Initiative Selection */}
      <div className="space-y-2">
        <TooltipLabel 
          htmlFor="sf-initiative" 
          tooltip={costManagementTooltips.strategicLinking?.initiative || 'Link to strategic initiative'}
        >
          Strategic Initiative
        </TooltipLabel>
        <Select 
          value={form.linked_initiative_id || '__none__'}
          disabled={!form.linked_objective_id}
          onValueChange={(val) => {
            setForm({
              ...form,
              linked_initiative_id: val === '__none__' ? '' : val,
              linked_revenue_stream_id: '',
              linked_product_id: ''
            });
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={form.linked_objective_id ? "Select initiative (optional)" : "Select objective first"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">No Link</SelectItem>
            {filteredInitiatives.map(init => (
              <SelectItem key={init.id} value={init.id}>
                {init.initiative_id} - {init.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!form.linked_objective_id && (
          <p className="text-xs text-gray-500">Select objective first to see initiatives</p>
        )}
      </div>

      {/* Revenue Stream Selection */}
      <div className="space-y-2">
        <TooltipLabel 
          htmlFor="sf-stream" 
          tooltip={costManagementTooltips.strategicLinking?.revenueStream || 'Link to revenue stream'}
        >
          Revenue Stream
        </TooltipLabel>
        <Select 
          value={form.linked_revenue_stream_id || '__none__'}
          disabled={!form.linked_initiative_id}
          onValueChange={(val) => {
            setForm({
              ...form,
              linked_revenue_stream_id: val === '__none__' ? '' : val,
              linked_product_id: ''
            });
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={form.linked_initiative_id ? "Select stream (optional)" : "Select initiative first"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">No Link</SelectItem>
            {filteredStreams.map(stream => (
              <SelectItem key={stream.id} value={stream.id}>
                {stream.stream_id} - {stream.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!form.linked_initiative_id && (
          <p className="text-xs text-gray-500">Select initiative first to see revenue streams</p>
        )}
      </div>

      {/* Product/Service Selection */}
      <div className="space-y-2">
        <TooltipLabel 
          htmlFor="sf-product" 
          tooltip="Link this cost to a specific product or service for detailed cost tracking and profitability analysis"
        >
          <span className="flex items-center">
            <Package className="h-3 w-3 mr-1" />
            Product/Service
          </span>
        </TooltipLabel>
        <Select 
          value={form.linked_product_id || '__none__'}
          disabled={!form.linked_revenue_stream_id}
          onValueChange={(val) => {
            setForm({
              ...form,
              linked_product_id: val === '__none__' ? '' : val
            });
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={form.linked_revenue_stream_id ? "Select product/service (optional)" : "Select revenue stream first"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">No Link</SelectItem>
            {filteredProducts.map(product => (
              <SelectItem key={product.id} value={product.id}>
                {product.product_id} - {product.name} {product.sku ? `(${product.sku})` : ''} - {product.type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!form.linked_revenue_stream_id && (
          <p className="text-xs text-gray-500">Select revenue stream first to see products/services</p>
        )}
        {form.linked_revenue_stream_id && filteredProducts.length === 0 && (
          <p className="text-xs text-amber-600">No products/services linked to this revenue stream yet. Add products in Strategic Framework section.</p>
        )}
      </div>
    </div>
  );
};

export default StrategicFrameworkLinker;
